import { Controller } from "@hotwired/stimulus"
import { prefersReducedMotion } from "../lib/motion"
import { NativeBridge } from "../lib/native_bridge"
import { onboardingPending } from "../lib/onboarding"
import { markSplashShown, splashExitHoldMs, splashExitTiming, splashFadeMs, splashHoldMs, splashMode, splashTimeScale } from "../lib/splash_policy"

// Phase 1 (frame draw + logo pop) ends here; phase 2 (icons) normally overlaps it from PHASE_2_START.
const PHASE_1_END = 0.6
const PHASE_2_START = 0.4

// gsap + DrawSVGPlugin are imported lazily in playAnimation() so the heavy gsap bundle stays out of the
// eager application.js entry — it is only needed for the one-time splash animation, not on every page.

/**
 * GSAP SVG Splash Screen Controller
 *
 * Manages the splash screen with GSAP DrawSVG animations:
 * - Phase 1: Frame + Logo reveal (0–0.6s)
 * - Phase 2: Icons + decorations (0.4–1.25s)
 * - Phase 3: Hold + shimmer (1.0–1.5s)
 *
 * Lifecycle (lib/splash_policy.js):
 * - Native apps: feed only, every launch, 1.5× speed, gone a beat after the animation ends (no phase 3 hold)
 * - Exit: the mark lifts and fades, then the backdrop (splashExitTiming)
 * - Browser: once per session, auto-hide after page load + minimum duration
 * - 5s fallback timeout
 * - prefers-reduced-motion respect
 *
 * Usage:
 *   data-controller="splash-gsap"
 *   data-splash-gsap-min-duration-value="2000"
 *   data-splash-gsap-fade-duration-value="500"
 *   data-splash-gsap-fallback-timeout-value="5000"
 *   data-splash-gsap-auto-hide-value="true"
 */
export default class extends Controller {
  static values = {
    minDuration: { type: Number, default: 2000 },
    fadeDuration: { type: Number, default: 500 },
    nativeFadeDuration: { type: Number, default: 200 },
    fallbackTimeout: { type: Number, default: 5000 },
    autoHide: { type: Boolean, default: true }
  }

  static targets = ["logo"]

  initialize() {
    this.startTime = Date.now()
    this.isHiding = false
    this.hideScheduled = false
    this.turboLoadHandler = null
    this.hideTimeout = null
    this.removeTimeout = null
    this.fallbackTimeout = null
    this.timeline = null
  }

  connect() {
    this.disconnected = false

    this.native = this.isNativeApp()
    if (splashMode({ native: this.native, mainPage: this.isMainPage() }) === "skip") {
      markSplashShown()
      this.skip()
      return
    }

    // A brand screen: the native tab bar stays out of it (the first-run intro takes it over when it follows).
    if (this.native) {
      NativeBridge.TabBar.hide()
      this.hidTabBar = true
    }

    // Android holds its launch screen over the first render: start (and time) the splash once it is on screen.
    if (window.__toas2tLaunchPending === true) {
      // The first frame after the reveal rasterises the whole page; start once it has been painted.
      this.boundLaunchRevealed = () => requestAnimationFrame(() => requestAnimationFrame(() => this.start()))
      window.addEventListener("native:launch-revealed", this.boundLaunchRevealed, { once: true })
      // Lift the launch cover as soon as the splash can play from its first frame.
      const ready = this.prefersReducedMotion() ? Promise.resolve() : this.loadAnimation()
      ready.then(() => { if (!this.disconnected) NativeBridge.Launch.ready() })
      return
    }
    this.start()
  }

  // Not playing here: let a held launch cover go straight away.
  skip() {
    this.element.remove()
    if (window.__toas2tLaunchPending === true) NativeBridge.Launch.ready()
  }

  start() {
    this.startTime = Date.now()
    // Reduced motion: show everything instantly, no animation (no gsap needed)
    if (this.prefersReducedMotion()) {
      this.showAllInstantly()
    } else {
      this.playAnimation()
    }

    if (this.autoHideValue) {
      this.setupAutoHide()
    }
  }

  disconnect() {
    this.disconnected = true
    if (this.boundLaunchRevealed) window.removeEventListener("native:launch-revealed", this.boundLaunchRevealed)
    if (this.hidTabBar && !this.isHiding) NativeBridge.TabBar.show()
    if (this.timeline) {
      this.timeline.kill()
      this.timeline = null
    }
    this.clearTimers()
    if (this.turboLoadHandler) {
      document.removeEventListener("turbo:load", this.turboLoadHandler)
      this.turboLoadHandler = null
    }
  }

  // --- Animation ---

  // Lazy-load gsap and build the timeline paused at its first frame (so a held splash shows that frame, not
  // the finished logo); playAnimation() starts it.
  loadAnimation() {
    this.animationReady ||= Promise.all([ import("gsap"), import("gsap/DrawSVGPlugin") ])
      .then(([ { default: gsap }, { DrawSVGPlugin } ]) => {
        gsap.registerPlugin(DrawSVGPlugin)
        this.gsap = gsap
        // The controller may have disconnected while gsap was loading.
        if (this.disconnected) return
        const handoff = window.__toas2tLaunchPending === true
        this.timeline = this.buildTimeline({ handoff, hold: !this.native })
        this.timeline.timeScale(splashTimeScale({ native: this.native }))
        // Run every tween's first-render setup (DrawSVG path lengths, style reads) now, not on the first frame, and
        // park where the native launch cover leaves off (frame + logo shown) when it is handing over.
        this.timeline.progress(1).seek(handoff ? PHASE_1_END : 0)
      })
    return this.animationReady
  }

  async playAnimation() {
    await this.loadAnimation()
    this.timeline?.play()
  }

  // handoff: the Android launch cover already played phase 1 (frame + logo), so phase 2 waits for its end and the
  // timeline is parked there (see loadAnimation). hold: phase 3 shimmer (browser only; native ends on phase 2).
  buildTimeline({ handoff = false, hold = true } = {}) {
    const tl = this.gsap.timeline({ paused: true })
    const el = this.element
    const later = handoff ? PHASE_1_END - PHASE_2_START : 0

    // Phase 1: Frame + Logo reveal (0–0.5s)
    tl.fromTo(el.querySelector("#logo-frame"),
      { drawSVG: "0%" },
      { drawSVG: "100%", duration: 0.5, ease: "power2.inOut" },
      0
    )

    if (this.hasLogoTarget) {
      tl.fromTo(this.logoTarget,
        { scale: 0.8, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.4, ease: "back.out(1.7)" },
        0.2
      )
    }

    tl.to(el.querySelector("#logo-frame"),
      { fillOpacity: 0.03, duration: 0.2 },
      0.4
    )

    // Phase 2: Icons + decorations (0.4–1.1s)
    tl.fromTo(el.querySelector("#location-pin"),
      { drawSVG: "75% 75%" },
      { drawSVG: "0% 100%", duration: 0.35, ease: "power2.out" },
      0.4 + later
    )

    tl.fromTo(el.querySelectorAll(".exchange-arrow"),
      { drawSVG: "0%" },
      { drawSVG: "100%", duration: 0.3, ease: "power2.out", stagger: 0.1 },
      0.5 + later
    )

    tl.fromTo(el.querySelector("#house-silhouette"),
      { drawSVG: "36% 36%" },
      { drawSVG: "0% 100%", duration: 0.3, ease: "power2.out" },
      0.6 + later
    )

    tl.fromTo(el.querySelector("#dog-silhouette"),
      { drawSVG: "0%" },
      { drawSVG: "100%", duration: 0.4, ease: "power2.out" },
      0.65 + later
    )

    tl.fromTo(el.querySelectorAll(".decorative-dot"),
      { scale: 0, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.15, ease: "back.out(3)", stagger: 0.05 },
      0.7 + later
    )

    // Bottom community nodes — elastic spring
    const nodes = el.querySelectorAll(".community-node")
    tl.fromTo(nodes,
      { attr: { r: 0 }, opacity: 0 },
      {
        attr: { r: this.gsap.utils.wrap([8, 7]) },
        opacity: 1,
        duration: 0.35,
        ease: "elastic.out(1, 0.5)",
        stagger: 0.06
      },
      0.8 + later
    )

    // Bottom connecting arc — DrawSVG from center
    tl.fromTo(el.querySelectorAll(".connect-arc"),
      { drawSVG: "50% 50%" },
      { drawSVG: "0% 100%", duration: 0.35, ease: "power2.out" },
      0.9 + later
    )

    if (!hold) return tl

    // Phase 3: Hold + shimmer (1.0–1.5s)
    tl.to(el.querySelector("#logo-frame"), {
      strokeOpacity: 0.6,
      duration: 0.25,
      yoyo: true,
      repeat: 1,
      ease: "sine.inOut"
    }, 1.0 + later)

    return tl
  }

  showAllInstantly() {
    // Logo
    if (this.hasLogoTarget) {
      this.logoTarget.style.opacity = "1"
    }

    // SVG elements
    const svgPaths = this.element.querySelectorAll("#logo-frame, #location-pin, .exchange-arrow, #house-silhouette, #dog-silhouette")
    svgPaths.forEach(el => {
      el.style.strokeDasharray = "none"
      el.style.opacity = "1"
    })

    const dots = this.element.querySelectorAll(".decorative-dot")
    dots.forEach(el => {
      el.style.opacity = "1"
      el.style.transform = "scale(1)"
    })

    this.element.querySelectorAll(".community-node").forEach(el => {
      el.style.opacity = "1"
    })

    this.element.querySelectorAll(".connect-arc").forEach(el => {
      el.style.strokeDasharray = "none"
    })
  }

  prefersReducedMotion() {
    return prefersReducedMotion()
  }

  // --- Lifecycle (preserved from splash_controller.js) ---

  isNativeApp() {
    return /Turbo Native/i.test(navigator.userAgent)
  }

  isMainPage() {
    const path = window.location.pathname
    return path === "/" || path === "/products" || path === "/products/"
  }

  setupAutoHide() {
    if (document.readyState === "complete") {
      this.scheduleHide()
    } else {
      window.addEventListener("load", () => this.scheduleHide(), { once: true })
    }

    this.turboLoadHandler = () => this.scheduleHide()
    document.addEventListener("turbo:load", this.turboLoadHandler)

    // Load events or the animation never finishing must not leave the splash up.
    this.fallbackTimeout = setTimeout(() => { if (!this.isHiding) this.hide() }, this.fallbackTimeoutValue)
  }

  scheduleHide() {
    if (this.isHiding || this.hideScheduled) return
    this.hideScheduled = true

    // Native: the splash ends with its animation (the page is already rendered underneath), after a beat to land.
    if (this.native) {
      this.animationFinished().then(() => {
        if (this.disconnected) return
        this.hideTimeout = setTimeout(() => this.hide(), splashExitHoldMs({ native: true, reduced: this.prefersReducedMotion() }))
      })
      return
    }

    const remaining = splashHoldMs({ native: false, minDurationMs: this.minDurationValue, elapsedMs: Date.now() - this.startTime })
    this.hideTimeout = setTimeout(() => this.hide(), remaining)
  }

  animationFinished() {
    if (this.prefersReducedMotion()) return Promise.resolve()
    return this.loadAnimation().then(() => new Promise((resolve) => {
      if (!this.timeline || this.timeline.progress() === 1) return resolve()
      this.timeline.eventCallback("onComplete", resolve)
    }))
  }

  hide() {
    if (this.isHiding) return
    this.isHiding = true

    markSplashShown()

    const totalMs = this.prefersReducedMotion() ? 0
      : splashFadeMs({ native: this.native, fadeMs: this.fadeDurationValue, nativeFadeMs: this.nativeFadeDurationValue })
    const { markMs, backdropDelayMs, backdropMs } = splashExitTiming(totalMs)
    this.element.style.setProperty("--splash-exit-mark", `${markMs}ms`)
    this.element.style.setProperty("--splash-exit-backdrop", `${backdropMs}ms`)
    this.element.style.setProperty("--splash-exit-delay", `${backdropDelayMs}ms`)
    this.element.classList.add("is-exiting")

    // The native tab bar comes back once the splash is gone, not over it mid-exit.
    const showTabBar = this.hidTabBar && !onboardingPending()
    this.removeTimeout = setTimeout(() => {
      this.element.remove()
      if (showTabBar) NativeBridge.TabBar.show()
    }, backdropDelayMs + backdropMs)
  }

  forceHide() {
    this.clearTimers()
    this.hide()
  }

  clearTimers() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    if (this.removeTimeout) {
      clearTimeout(this.removeTimeout)
      this.removeTimeout = null
    }
    if (this.fallbackTimeout) {
      clearTimeout(this.fallbackTimeout)
      this.fallbackTimeout = null
    }
  }
}
